import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Trash2, Plus } from "lucide-react";
import TokenManagement from "@/components/TokenManagement";
import UserManagement from "@/components/UserManagement";

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [ref, setRef] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [users, setUsers] = useState([]);

  const [newDefault, setNewDefault] = useState({ country: "", industry: "", import_dependency_default: 5, logistics_ease_default: 5, market_growth_default: 5 });
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "researcher" });

  const load = async () => {
    const [s, r, d] = await Promise.all([http.get("/settings"), http.get("/reference"), http.get("/country-industry-defaults")]);
    setSettings(s.data); setRef(r.data); setDefaults(d.data);
    if (user.role === "admin") {
      const u = await http.get("/auth/users");
      setUsers(u.data);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveSettings = async () => {
    try {
      await http.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const upsertDefault = async () => {
    try {
      await http.post("/country-industry-defaults", newDefault);
      toast.success("Default saved");
      setNewDefault({ ...newDefault, country: "", industry: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteDefault = async (row) => {
    if (!window.confirm(`Delete default for ${row.country} × ${row.industry}?`)) return;
    await http.delete("/country-industry-defaults", { params: { country: row.country, industry: row.industry } });
    load();
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await http.post("/auth/users", newUser);
      toast.success("User created");
      setNewUser({ email: "", password: "", name: "", role: "researcher" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!settings || !ref) return <Layout title="Settings"><div className="text-slate-500">Loading…</div></Layout>;

  return (
    <Layout title="Settings" subtitle="Weekly targets, country-industry defaults, users">
      <Tabs defaultValue="targets" className="w-full">
        <TabsList>
          <TabsTrigger value="targets" data-testid="tab-targets">Weekly Targets</TabsTrigger>
          <TabsTrigger value="ci-defaults" data-testid="tab-ci-defaults">Country × Industry</TabsTrigger>
          {user.role === "admin" && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          {user.role === "admin" && <TabsTrigger value="api-tokens" data-testid="tab-api-tokens">API Tokens</TabsTrigger>}
        </TabsList>

        <TabsContent value="targets" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5 max-w-2xl">
            <div className="text-sm text-slate-600 p-3 rounded-md bg-slate-50 border border-slate-200">
              These constants drive weekly performance scoring. Keep them conservative for the first 3–4 weeks (calibration period), then raise based on real baseline data.
            </div>
            {[
              ["minimum_required_fields",         "Minimum scoring fields to calculate score (out of 11)", 1, 11],
              ["target_qualified_leads_per_week", "Target qualified leads / week",                        1, null],
              ["target_companies_per_week",       "Target companies researched / week",                    1, null],
              ["target_followups_per_week",       "Target follow-ups / week",                              0, null],
              ["target_pipeline_value_per_week",  "Target pipeline value / week (USD)",                    0, null],
            ].map(([k, label, min, max]) => (
              <div key={k}>
                <Label className="text-xs uppercase font-semibold text-slate-600 tracking-wider">{label}</Label>
                <Input type="number"
                       min={min} max={max ?? undefined}
                       value={settings[k]}
                       onChange={(e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) || 0 })}
                       data-testid={`settings-${k}`}
                       className="mt-1.5 font-mono text-lg font-semibold max-w-xs" />
              </div>
            ))}
            <Button onClick={saveSettings} data-testid="btn-save-settings" className="bg-amber-600 hover:bg-amber-700 text-white">Save targets</Button>
          </div>
        </TabsContent>

        <TabsContent value="ci-defaults" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="text-sm text-slate-600 p-3 rounded-md bg-slate-50 border border-slate-200 mb-4">
              Set once per Country × Industry pair. Auto-applied to new leads and can be overridden per-lead with justification (§4).
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Country</Label>
                <Select value={newDefault.country} onValueChange={(v) => setNewDefault({ ...newDefault, country: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ci-country"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{ref.all_countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Industry</Label>
                <Select value={newDefault.industry} onValueChange={(v) => setNewDefault({ ...newDefault, industry: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ci-industry"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{ref.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {["import_dependency_default", "logistics_ease_default", "market_growth_default"].map((k) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">{k.replace(/_/g, " ").replace(" default", "")}</Label>
                  <Input type="number" min={1} max={10} value={newDefault[k]}
                         onChange={(e) => setNewDefault({ ...newDefault, [k]: Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                         data-testid={`ci-${k}`}
                         className="mt-1.5 font-mono font-semibold text-center" />
                </div>
              ))}
            </div>
            <Button onClick={upsertDefault} disabled={!newDefault.country || !newDefault.industry}
                    data-testid="btn-add-ci-default"
                    className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"><Plus className="w-4 h-4 mr-1" /> Save default</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-3 font-semibold">Country</th>
                  <th className="text-left px-4 py-3 font-semibold">Industry</th>
                  <th className="text-center px-4 py-3 font-semibold">Import Dep.</th>
                  <th className="text-center px-4 py-3 font-semibold">Logistics</th>
                  <th className="text-center px-4 py-3 font-semibold">Growth</th>
                  <th className="text-left px-4 py-3 font-semibold">Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {defaults.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500" data-testid="ci-empty">No defaults set yet.</td></tr>
                )}
                {defaults.map((d) => (
                  <tr key={`${d.country}-${d.industry}`} className="text-sm">
                    <td className="px-4 py-2.5 text-slate-800 font-medium">{d.country}</td>
                    <td className="px-4 py-2.5 text-slate-700">{d.industry}</td>
                    <td className="px-4 py-2.5 text-center font-mono font-semibold text-slate-900">{d.import_dependency_default}</td>
                    <td className="px-4 py-2.5 text-center font-mono font-semibold text-slate-900">{d.logistics_ease_default}</td>
                    <td className="px-4 py-2.5 text-center font-mono font-semibold text-slate-900">{d.market_growth_default}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{d.last_updated_by_name}<br /><span className="text-slate-400">{new Date(d.last_updated_date).toLocaleDateString()}</span></td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => deleteDefault(d)} className="text-rose-600 hover:text-rose-800 p-1" data-testid={`delete-ci-${d.country}-${d.industry}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {user.role === "admin" && (
          <TabsContent value="users" className="mt-4">
            <UserManagement />
          </TabsContent>
        )}

        {user.role === "admin" && (
          <TabsContent value="api-tokens" className="mt-4">
            <TokenManagement integrationUsers={users} />
          </TabsContent>
        )}
      </Tabs>
    </Layout>
  );
}
